// Utility functions for move preview confirm messages

/**
 * Get the source location description (Step or Substep with correct numbering)
 * @param {Object} locationState - The location.state object
 * @returns {string} - Formatted source location string
 */
export const getSourceLocation = (locationState) => {
  const {
    sourceStepNumber,
    sourceSubstepIndex,
    sourceStepIndex,
    fromSubstepLetter,
  } = locationState;

  // If sourceSubstepIndex is defined, we're coming from a substep
  if (sourceSubstepIndex !== undefined) {
    const substepLetter =
      fromSubstepLetter || String.fromCharCode(65 + sourceSubstepIndex);
    const stepNumber = (sourceStepIndex || 0) + 1;
    return `Substep ${substepLetter} in Step ${stepNumber}`;
  }

  // Otherwise, we're coming from a step
  const stepNumber = sourceStepNumber || (sourceStepIndex || 0) + 1;
  return `Step ${stepNumber}`;
};

/**
 * Get the target location description
 * @param {Object} locationState - The location.state object
 * @param {string} moveType - The type of move ("up", "below", "into")
 * @param {number} targetStepIndex - The target step index
 * @param {Object} targetSectionData - The target section data
 * @returns {string} - Formatted target location string
 */
export const getTargetLocation = (
  locationState,
  moveType,
  targetStepIndex,
  targetSectionData
) => {
  if (moveType === "into") {
    const stepNumber = parseInt(targetStepIndex || 0) + 1;
    // If moving into a substep, include the substep information
    if (locationState.targetSubstepIndex !== undefined) {
      const substepLetter = String.fromCharCode(
        65 + locationState.targetSubstepIndex
      );
      return `Substep ${substepLetter} in Step ${stepNumber}`;
    }
    return `Step ${stepNumber}`;
  }

  const {
    targetNodeType,
    targetNodeIndex,
    targetNodeGlobalNumber,
    targetSubstepIndex,
  } = locationState;
  const stepNumber = targetStepIndex + 1;

  // Check if target is a substep (targetSubstepIndex is defined)
  if (targetSubstepIndex !== undefined) {
    const substepLetter = String.fromCharCode(65 + targetSubstepIndex);
    return `Substep ${substepLetter} in Step ${stepNumber}`;
  }

  if (targetNodeType && targetNodeIndex !== undefined) {
    const nodeNumber = targetNodeIndex + 1;
    return `${targetNodeType} ${nodeNumber} in Step ${stepNumber}`;
  }

  // Fallback to global number if local index is not available
  if (targetNodeGlobalNumber) {
    return `${targetNodeType} ${targetNodeGlobalNumber} in Step ${stepNumber}`;
  }

  return `Step ${stepNumber}`;
};

/**
 * Get the move direction description
 * @param {string} moveType - The type of move
 * @param {boolean} isMovingNode - Whether we're moving a node or step/section
 * @returns {string} - Formatted move direction string
 */
export const getMoveDirection = (
  moveType,
  isMovingNode = true,
  targetSubstepIndex = undefined
) => {
  switch (moveType) {
    case "up":
      return "Above";
    case "below":
      return "Below";
    // case "into":
    //   if (isMovingNode && targetSubstepIndex !== undefined) {
    //     const substepLetter = String.fromCharCode(65 + targetSubstepIndex);
    //     return `To End of Substep ${substepLetter}`;
    //   }
    //   return isMovingNode ? "To End of" : "To End of Section";
    default:
      return "";
  }
};

/**
 * Generate confirm move message for nodes (Rich Text, Attachment, Canvas, Video)
 * @param {Object} locationState - The location.state object
 * @param {string} nodeType - The type of node being moved
 * @param {string} moveType - The type of move
 * @param {number} targetStepIndex - The target step index
 * @param {Object} targetSectionData - The target section data
 * @returns {string} - Complete confirm move message
 */
export const generateNodeMoveMessage = (
  locationState,
  nodeType,
  moveType,
  targetStepIndex,
  targetSectionData
) => {
  const sourceLocation = getSourceLocation(locationState);

  // Get the node number from various possible sources
  // Priority: 1. title parsing, 2. sourceNodeIndex, 3. sourceNodeGlobalNumber
  let nodeNumber = null;

  // Try to extract from title first (most reliable for child node number)
  if (locationState.title) {
    // Handle different title formats:
    // "Moving Canvas 1 from Step 2" -> extract "Canvas 1"
    // "Moving Attachment 8 from Step 3" -> extract "Attachment 8"
    // "Moving Video 5 from Step 1" -> extract "Video 5"
    // "Moving Rich Text 3 from Step 2" -> extract "Rich Text 3"

    // First try to match the full pattern
    const titleMatch = locationState.title.match(
      /Moving\s+(\w+(?:\s+\w+)?\s+\d+)/i
    );
    if (titleMatch && titleMatch[1]) {
      // Extract just the number part for the node number
      const numberMatch = titleMatch[1].match(/\d+$/);
      if (numberMatch) {
        nodeNumber = parseInt(numberMatch[0], 10);
      }
    }

    // If that didn't work, try alternative patterns
    if (!nodeNumber) {
      // Try to find any word followed by a number
      const altMatch = locationState.title.match(/(\w+(?:\s+\w+)?)\s+(\d+)/i);
      if (altMatch && altMatch[2]) {
        nodeNumber = parseInt(altMatch[2], 10);
      }
    }
  }

  // Fallback to other sources if title parsing failed
  if (!nodeNumber) {
    nodeNumber =
      (locationState.sourceNodeIndex !== undefined
        ? locationState.sourceNodeIndex + 1
        : null) ||
      locationState.sourceNodeGlobalNumber ||
      1;
  }

  // Get the source step number from the tree path or location state
  let sourceStepNumber = null;

  // Try to get from title first (e.g., "from Step 2")
  if (locationState.title) {
    // Try multiple patterns for step number extraction
    let stepMatch = locationState.title.match(/from\s+Step\s+(\d+)/i);
    if (stepMatch && stepMatch[1]) {
      sourceStepNumber = parseInt(stepMatch[1], 10) - 1;
    } else {
      // Try alternative patterns
      stepMatch = locationState.title.match(/Step\s+(\d+)/i);
      if (stepMatch && stepMatch[1]) {
        sourceStepNumber = parseInt(stepMatch[1], 10);
      }
    }
  }
  
  // Fallback to location state values
  if (!sourceStepNumber) {
    sourceStepNumber =
      locationState.sourceStepNumber ||
      (locationState.sourceStepIndex !== undefined
        ? locationState.sourceStepIndex + 1
        : null) ||
      (locationState.stepNumber
        ? parseInt(locationState.stepNumber, 10)
        : null);
  }

  // Build the source location string with proper step number
  let sourceLocationString = "";
  if (locationState.sourceSubstepIndex !== undefined) {
    const substepLetter = String.fromCharCode(
      65 + locationState.sourceSubstepIndex
    );
    // Show Substep as "StepNumberLetter", e.g., Substep 1C
    sourceLocationString = `Substep ${sourceStepNumber}${substepLetter} in Step ${sourceStepNumber}`;
  } else {
    sourceLocationString = `Step ${sourceStepNumber}`;
  }

  // Handle "into" moves specially to include step number in the direction
  if (moveType === "into" && locationState.targetSubstepIndex !== undefined) {
    const substepLetter = String.fromCharCode(
      65 + locationState.targetSubstepIndex
    );
    const stepNumber = parseInt(targetStepIndex || 0) + 1;
    return `Confirm Move of ${nodeType} ${nodeNumber} from ${sourceLocationString}`;
  }

  const moveDirection = getMoveDirection(
    moveType,
    true,
    locationState.targetSubstepIndex
  );
  const targetLocation = getTargetLocation(
    locationState,
    moveType,
    targetStepIndex,
    targetSectionData
  );
  // ${moveDirection} ${targetLocation}

  return `Confirm Move of ${nodeType} ${nodeNumber} from ${sourceLocationString}`;
};

/**
 * Generate confirm move message for steps
 * @param {number} sourceStepIndex - The source step index
 * @param {string} moveType - The type of move
 * @param {number} targetStepIndex - The target step index
 * @param {Object} targetSectionData - The target section data
 * @param {number} targetSectionIndex - The target section index
 * @returns {string} - Complete confirm move message
 */
export const generateStepMoveMessage = (
  sourceStepIndex,
  moveType,
  targetStepIndex,
  targetSectionData,
  targetSectionIndex
) => {
  const stepNumber = sourceStepIndex + 1;
  const moveDirection = getMoveDirection(moveType, false);

  if (moveType === "into") {
    const sectionNumber = targetSectionIndex + 1;
    return `Confirm Move of Step ${stepNumber} ${moveDirection} ${sectionNumber}`;
  }

  const targetStepNumber =
    targetSectionData?.globalStepNumber ||
    targetSectionData?.stepIndex + 1 ||
    targetStepIndex + 1;

  return `Confirm Move of Step ${stepNumber} ${moveDirection} Step ${targetStepNumber}`;
};

/**
 * Generate confirm move message for sections
 * @param {number} sourceSectionIndex - The source section index
 * @param {string} moveType - The type of move
 * @param {number} targetSectionIndex - The target section index
 * @returns {string} - Complete confirm move message
 */
export const generateSectionMoveMessage = (
  sourceSectionIndex,
  moveType,
  targetSectionIndex
) => {
  const sourceSectionNumber = sourceSectionIndex + 1;
  const targetSectionNumber = targetSectionIndex + 1;
  const moveDirection = getMoveDirection(moveType, false);

  return `Confirm Move of Section ${sourceSectionNumber} ${moveDirection} Section ${targetSectionNumber}`;
};

/**
 * Main function to generate the complete confirm move message
 * @param {Object} locationState - The location.state object
 * @param {string} nodeType - The type of node being moved (for node moves)
 * @param {string} moveType - The type of move
 * @param {number} sourceStepIndex - The source step index
 * @param {number} targetStepIndex - The target step index
 * @param {Object} targetSectionData - The target section data
 * @param {number} sourceSectionIndex - The source section index
 * @param {number} targetSectionIndex - The target section index
 * @returns {string} - Complete confirm move message
 */
export const generateConfirmMoveMessage = ({
  locationState,
  nodeType,
  moveType,
  sourceStepIndex,
  targetStepIndex,
  targetSectionData,
  sourceSectionIndex,
  targetSectionIndex,
}) => {
  if (locationState.movingNode) {
    return generateNodeMoveMessage(
      locationState,
      nodeType,
      moveType,
      targetStepIndex,
      targetSectionData
    );
  }

  if (locationState.movingStep) {
    return generateStepMoveMessage(
      sourceStepIndex,
      moveType,
      targetStepIndex,
      targetSectionData,
      targetSectionIndex
    );
  }

  return generateSectionMoveMessage(
    sourceSectionIndex,
    moveType,
    targetSectionIndex
  );
};
